import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import './index.scss';
import img_components_sitetopnav_default_logo_src from '@/assets/images/components-sitetopnav-default-logo-src.png';
import img_components_sitetopnav_light_logo_src from '@/assets/images/components-sitetopnav-light-logo-src.png';

const DEFAULT_LOGO_SRC =
  img_components_sitetopnav_default_logo_src;

/** Colored wordmark + icon (light header) */
const LIGHT_LOGO_SRC =
  img_components_sitetopnav_light_logo_src;

const HamburgerIcon = props => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false" {...props}>
    <path
      d="M4 7h16M4 12h16M4 17h16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const CloseIcon = props => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false" {...props}>
    <path
      d="M6 6l12 12M18 6L6 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const NavShell = ({ className, open, setOpen, items, logoSrc }) => {
  return (
    <nav className={[className, open ? 'is-open' : ''].filter(Boolean).join(' ')} aria-label="Primary">
      <div className="site-topnav__bar">
        <NavLink className="site-topnav__logo" to="/" aria-label="Sakura Floors">
          <img
            className="site-topnav__logo-img"
            alt=""
            referrerPolicy="no-referrer"
            src={logoSrc}
          />
        </NavLink>

        <div className="site-topnav__links" aria-label="Desktop navigation">
          {items.map(item => (
            <NavLink
              key={item.to}
              className={({ isActive }) =>
                ['site-topnav__link', isActive ? 'is-active' : ''].filter(Boolean).join(' ')
              }
              to={item.to}
              end={item.end}
            >
              {item.label}
            </NavLink>
          ))}
        </div>

        <button
          className="site-topnav__menu-btn"
          type="button"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen(v => !v)}
        >
          {open ? <CloseIcon /> : <HamburgerIcon />}
        </button>
      </div>
    </nav>
  );
};

const SiteTopNav = ({ className = '', logoSrc, variant = 'default' }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showSticky, setShowSticky] = useState(false);

  const resolvedLogoSrc = logoSrc ?? (variant === 'light' ? LIGHT_LOGO_SRC : DEFAULT_LOGO_SRC);
  const isLight = variant === 'light';
  const variantClass = isLight ? 'site-topnav--light' : '';
  const overlayClass = isLight ? '' : 'site-topnav--overlay';

  const items = useMemo(
    () => [
      { to: '/', label: 'HOME', end: true },
      { to: '/products', label: 'PRODUCTS' },
      { to: '/resources', label: 'RESOURCES' },
      { to: '/contact-us', label: 'CONTACT US' },
      { to: '/about-us', label: 'ABOUT US' },
    ],
    []
  );

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    let raf = 0;
    const onScrollOrResize = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const threshold = window.innerHeight * 0.6;
        setShowSticky(window.scrollY > threshold);
      });
    };
    onScrollOrResize();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, []);

  const stickyNav = (
    <NavShell
      className={['site-topnav', 'site-topnav--sticky', variantClass, showSticky ? 'is-visible' : '']
        .filter(Boolean)
        .join(' ')}
      open={open && showSticky}
      setOpen={setOpen}
      items={items}
      logoSrc={resolvedLogoSrc}
    />
  );

  return (
    <>
      <NavShell
        className={['site-topnav', overlayClass, variantClass, className].filter(Boolean).join(' ')}
        open={open}
        setOpen={setOpen}
        items={items}
        logoSrc={resolvedLogoSrc}
      />
      {typeof document !== 'undefined' ? createPortal(stickyNav, document.body) : stickyNav}

      <div
        className={['site-topnav__mask', isLight ? 'site-topnav__mask--light' : '', open ? 'is-open' : '']
          .filter(Boolean)
          .join(' ')}
        aria-hidden={!open}
        onClick={e => {
          if (e.target === e.currentTarget) setOpen(false);
        }}
      >
        <div className="site-topnav__mask-inner" role="dialog" aria-label="Mobile menu">
          <div className="site-topnav__mask-topbar">
            <div className="site-topnav__mask-icon" aria-hidden="true">
              <img
                className="site-topnav__mask-logo"
                alt=""
                referrerPolicy="no-referrer"
                src={resolvedLogoSrc}
              />
            </div>
            <button
              className="site-topnav__mask-close"
              type="button"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </button>
          </div>

          <div className="site-topnav__mobile-panel" role="menu" aria-label="Mobile navigation">
            {items.map(item => (
              <NavLink
                key={item.to}
                role="menuitem"
                className={({ isActive }) =>
                  ['site-topnav__mobile-link', isActive ? 'is-active' : ''].filter(Boolean).join(' ')
                }
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default SiteTopNav;
export { LIGHT_LOGO_SRC };
